// src/components/ChatBubble.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaComments, FaPaperPlane, FaTimes, FaBolt, FaCheckCircle } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

/**
 * ChatBubble (Upgraded)
 * - Premium, glass + gradient styling aligned with the upgraded site (Home/About/Programs/etc)
 * - Accessible: ESC closes, focus input on open, Enter to send (Shift+Enter newline)
 * - Keeps your keyword-based KB logic (no backend)
 */

const knowledgeBase = {
  about:
    "The Institute of Bankers Gambia (IBG) is owned by the Gambia Bankers Association (GBA). It sets banking standards, maintains registers, conducts exams, and promotes ethics and professionalism in banking.",
  programs:
    "IBG offers Diploma, Intermediate Professional, and Chartered Banker pathways. We also offer Banking Certifications, Short Courses, Ethics & Compliance, and Capacity Building programmes.",
  timeline:
    "Admissions typically start in July with applications, followed by entrance exams in August, interviews in September, admission decisions in October, and orientation in November.",
  faq:
    "Anyone meeting eligibility criteria can apply. Most programs are in-person (hybrid options may be introduced). Membership benefits start after registration and fee payment.",
  contact:
    "You can reach IBG via email at info@ibgambia.com or call +220 2273987 / 2025336. The office is in The Gambia.",
};

const contextKeywords = [
  { keys: ["about", "who", "what", "information", "ibg"], category: "about" },
  { keys: ["program", "course", "admission", "join", "apply", "study", "certification", "diploma"], category: "programs" },
  { keys: ["timeline", "when", "schedule", "dates", "month", "intake"], category: "timeline" },
  { keys: ["faq", "question", "how", "eligibility", "benefits", "requirement"], category: "faq" },
  { keys: ["contact", "email", "phone", "address", "reach", "call"], category: "contact" },
];

const fallbackResponses = [
  "I’m here to help. Ask me about programmes, admissions, timelines, or contact info.",
  "Can you clarify what you need? I can guide you on programmes, eligibility, or how to apply.",
  "Ask me anything about IBG — programmes, admissions steps, or the timeline.",
];

const ChatBubble = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([
    {
      type: "ai",
      text: "Hi — I’m the IBG Assistant. Ask me about programmes, admissions, timelines, or contact info.",
    },
  ]);

  const inputRef = useRef(null);

  const quickChips = useMemo(
    () => [
      { label: "How do I apply?", prompt: "How do I apply for IBG programmes?" },
      { label: "Programmes", prompt: "What programmes does IBG offer?" },
      { label: "Admissions timeline", prompt: "What is the admissions timeline?" },
      { label: "Contact", prompt: "How can I contact IBG?" },
    ],
    []
  );

  const generateResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();

    const scores = contextKeywords.map(({ keys, category }) => {
      let score = 0;
      keys.forEach((k) => {
        if (msg.includes(k)) score += 1;
      });
      return { category, score };
    });

    const bestMatch = scores.reduce(
      (prev, cur) => (cur.score > prev.score ? cur : prev),
      { category: null, score: 0 }
    );

    if (bestMatch.score > 0) return knowledgeBase[bestMatch.category];
    return fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
  };

  const close = () => setOpen(false);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // focus input on open
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [open]);

  const pushUserMessage = (text) => {
    const userMessage = text.trim();
    if (!userMessage) return;

    const updated = [...chatHistory, { type: "user", text: userMessage }];
    setChatHistory(updated);

    // AI response after slight delay
    window.setTimeout(() => {
      const aiResponse = generateResponse(userMessage);
      setChatHistory([...updated, { type: "ai", text: aiResponse }]);
    }, 420);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const userMessage = message.trim();
    if (!userMessage) return;
    setMessage("");
    pushUserMessage(userMessage);
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-label={open ? "Close chat" : "Chat with IBG assistant"}
        whileHover={{ y: -2 }}
        whileTap={{ y: 0 }}
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1400,
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: "none",
          color: "var(--ibg-white)",
          background: "linear-gradient(135deg, var(--ibg-navy) 0%, var(--ibg-blue) 60%, var(--ibg-blue-light) 100%)",
          boxShadow: "0 18px 40px rgba(13,110,253,0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        className="btn"
      >
        {open ? <FaTimes size={20} /> : <FaComments size={22} />}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 26, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 26, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
              position: "fixed",
              bottom: 88,
              right: 20,
              zIndex: 1400,
              width: "min(380px, calc(100vw - 40px))",
              maxHeight: "min(560px, calc(100vh - 140px))",
              display: "flex",
              flexDirection: "column",
              borderRadius: 28,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.16)",
              boxShadow: "0 30px 90px rgba(2, 6, 23, 0.35)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(248,251,255,0.92) 100%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
            }}
            className="chat-box"
            role="dialog"
            aria-modal="true"
            aria-label="IBG Assistant chat"
          >
            {/* Top accent strip */}
            <div
              aria-hidden="true"
              style={{
                height: 6,
                background: "linear-gradient(90deg, var(--ibg-navy) 0%, var(--ibg-blue) 60%, var(--ibg-blue-light) 100%)",
              }}
            />

            {/* Header */}
            <div
              className="d-flex align-items-start justify-content-between gap-2"
              style={{
                padding: "14px 16px 10px",
                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                background: "rgba(255,255,255,0.65)",
              }}
            >
              <div>
                <div className="d-flex align-items-center gap-2">
                  <div
                    aria-hidden="true"
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 14,
                      background: "linear-gradient(135deg, rgba(13,110,253,0.12), rgba(11,59,145,0.18))",
                      color: "var(--ibg-blue)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FaBolt />
                  </div>
                  <div className="lh-sm">
                    <div style={{ fontWeight: 900, color: "var(--ibg-ink)", letterSpacing: "-0.02em" }}>IBG Assistant</div>
                    <div style={{ color: "var(--ibg-slate-soft)", fontWeight: 700, fontSize: "0.86rem" }}>
                      Quick answers • No login
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={close}
                className="btn"
                aria-label="Close chat"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 14,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  background: "rgba(15, 23, 42, 0.03)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Quick chips */}
            <div
              className="d-flex gap-2 flex-wrap"
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                background: "rgba(255,255,255,0.55)",
              }}
            >
              {quickChips.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className="btn"
                  onClick={() => pushUserMessage(c.prompt)}
                  style={{
                    borderRadius: 999,
                    padding: "8px 10px",
                    border: "1px solid rgba(13,110,253,0.16)",
                    background: "rgba(13,110,253,0.10)",
                    color: "var(--ibg-navy)",
                    fontWeight: 900,
                    fontSize: "0.82rem",
                    lineHeight: 1,
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* Chat history */}
            <div
              className="flex-grow-1 overflow-auto"
              style={{
                padding: 14,
                background:
                  "radial-gradient(circle at 10% 10%, rgba(13,110,253,0.06), transparent 42%), radial-gradient(circle at 90% 20%, rgba(102,178,255,0.05), transparent 45%)",
              }}
            >
              {chatHistory.map((msg, idx) => {
                const isUser = msg.type === "user";
                return (
                  <div
                    key={idx}
                    className={`d-flex mb-2 ${isUser ? "justify-content-end" : "justify-content-start"}`}
                  >
                    <div
                      style={{
                        maxWidth: "82%",
                        borderRadius: 18,
                        padding: "10px 12px",
                        lineHeight: 1.6,
                        fontSize: "0.92rem",
                        fontWeight: 700,
                        color: isUser ? "var(--ibg-white)" : "var(--ibg-ink)",
                        background: isUser
                          ? "linear-gradient(135deg, var(--ibg-navy) 0%, var(--ibg-blue) 70%, var(--ibg-blue-light) 110%)"
                          : "rgba(255,255,255,0.92)",
                        border: isUser ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(15, 23, 42, 0.08)",
                        boxShadow: isUser
                          ? "0 14px 34px rgba(13,110,253,0.18)"
                          : "0 10px 24px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      {!isUser && (
                        <div className="d-flex align-items-center gap-2 mb-1" style={{ color: "var(--ibg-blue)" }}>
                          <FaCheckCircle aria-hidden="true" />
                          <span style={{ fontSize: "0.82rem", fontWeight: 900, color: "var(--ibg-navy)" }}>IBG Assistant</span>
                        </div>
                      )}
                      <div style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Composer */}
            <form
              onSubmit={handleSubmit}
              style={{
                padding: 12,
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                background: "rgba(255,255,255,0.70)",
              }}
            >
              <div className="d-flex gap-2 align-items-end">
                <textarea
                  ref={inputRef}
                  className="form-control"
                  placeholder="Type your message…"
                  rows={2}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  aria-label="Chat message"
                  style={{
                    resize: "none",
                    borderRadius: 16,
                    minHeight: 52,
                    border: "1px solid rgba(15, 23, 42, 0.10)",
                    fontWeight: 700,
                  }}
                />

                <motion.button
                  type="submit"
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                  className="btn"
                  aria-label="Send message"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 16,
                    border: "none",
                    color: "var(--ibg-white)",
                    background: "linear-gradient(135deg, var(--ibg-navy) 0%, var(--ibg-blue) 60%, var(--ibg-blue-light) 100%)",
                    boxShadow: "0 18px 40px rgba(13,110,253,0.18)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaPaperPlane />
                </motion.button>
              </div>

              <div className="mt-2" style={{ color: "var(--ibg-slate-soft)", fontSize: "0.82rem", fontWeight: 700 }}>
                Tip: Press <strong>Enter</strong> to send, <strong>Shift + Enter</strong> for a new line.
              </div>
            </form>

            <style>{`
              .form-control:focus {
                border-color: rgba(13,110,253,0.35) !important;
                box-shadow: 0 0 0 3px rgba(13,110,253,0.12) !important;
              }
              .btn:focus {
                box-shadow: 0 0 0 3px rgba(13,110,253,0.18) !important;
              }
            `}</style>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBubble;